import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert as MuiAlert,
  Divider,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import api from "../../services/api";

const SEVERITY_COLOR = {
  HIGH: "error",
  MEDIUM: "warning",
  LOW: "default",
};

function KpiCard({ icon, label, value, color = "primary.main" }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: `${color}15`,
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [attentionQueue, setAttentionQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // Sequential, not Promise.all — /v1/dashboard/summary is the one
        // place alert generation actually runs now (see DashboardService).
        // Awaiting it first means its inserts have already committed by
        // the time attention-queue is fetched, so that second call reads
        // fresh alerts instead of racing the same generation logic in
        // parallel, which was the actual source of the duplicate alerts
        // seen earlier.
        const summaryRes = await api.get("/v1/dashboard/summary");
        const queueRes = await api.get("/v1/dashboard/attention-queue");
        if (!cancelled) {
          setSummary(summaryRes.data);
          setAttentionQueue(queueRes.data);
        }
      } catch (err) {
        console.error("Failed to load dashboard:", err);
        if (!cancelled) setError("Failed to load dashboard data. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Container sx={{ mt: 6, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading dashboard…
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <MuiAlert severity="error">{error}</MuiAlert>
      </Container>
    );
  }

  const ckdStageEntries = Object.entries(summary?.byCkdStage || {}).sort(
    (a, b) => a[0].localeCompare(b[0])
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Dashboard
          </Typography>
          <Typography color="text.secondary">
            How your practice's patients are doing today
          </Typography>
        </Box>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate("/patients")}
        >
          View All Patients
        </Button>
      </Box>

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            icon={<PeopleAltIcon />}
            label="Total Patients"
            value={summary?.totalPatients ?? 0}
            color="#1A5EA8"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            icon={<WarningAmberIcon />}
            label="Need Attention"
            value={summary?.patientsNeedingAttention ?? 0}
            color="#D97706"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            icon={<PersonAddAlt1Icon />}
            label="New in Last 30 Days"
            value={summary?.newPatientsLast30Days ?? 0}
            color="#059669"
          />
        </Grid>
      </Grid>

      {/* CKD stage breakdown */}
      {ckdStageEntries.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Patients by CKD Stage
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            {ckdStageEntries.map(([stage, count]) => (
              <Chip key={stage} label={`Stage ${stage}: ${count}`} variant="outlined" />
            ))}
          </Box>
        </Paper>
      )}

      {/* Attention queue */}
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Attention Queue
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Patients with active alerts — overdue labs, critical values, or unresolved appointments
        </Typography>

        {attentionQueue.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">
              Nothing needs attention right now.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {attentionQueue.map((row, idx) => (
              <Box key={row.patient?.id ?? idx}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                    py: 1.5,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                    borderRadius: 1,
                    px: 1,
                  }}
                  onClick={() => navigate(`/patients/${row.patient?.id}`)}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Typography fontWeight={600}>
                        {row.patient?.firstName} {row.patient?.lastName}
                      </Typography>
                      {row.patient?.ckdStage && (
                        <Chip size="small" label={`Stage ${row.patient.ckdStage}`} variant="outlined" />
                      )}
                      <Chip
                        size="small"
                        label={row.highestSeverity}
                        color={SEVERITY_COLOR[row.highestSeverity] || "default"}
                      />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                      {row.alerts?.map((alert) => (
                        <Typography key={alert.id} variant="body2" color="text.secondary">
                          • {alert.title}
                          {alert.description ? ` — ${alert.description}` : ""}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                  <Button size="small" variant="text">
                    View
                  </Button>
                </Box>
                {idx < attentionQueue.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
}